import React, { useEffect, useMemo, useState } from "react";
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPeriod } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, SrTable } from "./components";
import { regionName, ORIGIN_NAMES_IT } from "./regionNames";
import { pulseGet } from "./api";
import { withForecastRows, forecastLayers, ForecastTooltip, ForecastNote } from "./forecast";

const ORIGINS = [
  ["DEU", "Germany"], ["USA", "United States"], ["FRA", "France"], ["GBR", "United Kingdom"], ["CHE", "Switzerland"],
  ["AUT", "Austria"], ["ESP", "Spain"], ["CAN", "Canada"], ["JPN", "Japan"], ["RUS", "Russia"],
];
const VIEWS = ["regions", "origins", "national"];

const unitNoteFor = (item, lang) => {
  const scale = item.unit_mult === "3" ? (lang === "it" ? "migliaia" : "thousands")
    : (item.unit_mult === "6" ? (lang === "it" ? "milioni" : "millions") : "");
  if (item.unit === "EUR") return lang === "it" ? `${scale || "EUR"}${scale ? " di EUR" : ""}` : `EUR ${scale}`.trim();
  return scale;
};

const MiniForecastChart = ({ item, dm, lang, t }) => {
  const T = tokens(dm);
  const fc = item.forecast;
  const observed = (item.observed || []).map((p) => ({ period: fmtPeriod(p.period, lang), raw: p.period, value: p.value }));
  const rows = useMemo(() => withForecastRows(observed, fc, lang), [item, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const title = (item.label || "").replace(/\s*\((EUR|US\$)\)\s*$/, "");
  const unitNote = unitNoteFor(item, lang);
  return (
    <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}
      data-testid={`italy-forecast-chart-${item.indicator}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: T.muted }}>
        {title}{unitNote ? ` (${unitNote})` : ""}
      </p>
      {!observed.length && <NoData dm={dm} h={120} />}
      {observed.length > 0 && (
        <div style={{ height: 190 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={T.border} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
              <YAxis tickFormatter={(v) => fmtCompact(v, lang)} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
              <Tooltip content={<ForecastTooltip dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
              <Bar dataKey="value" name={t("fc_observed")} fill={T.brand} isAnimationActive={false} />
              {forecastLayers(fc, t)}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-2">
        <SourceChip source="BANCA_D_ITALIA" dataset={item.freq === "M" ? "bop_travel" : "turismo_tavole"} period={observed.slice(-1)[0]?.raw} dm={dm} />
      </div>
      {fc ? <ForecastNote fc={fc} dm={dm} testid={`italy-forecast-note-${item.indicator}`} />
        : <p className="text-[11px] mt-2" style={{ color: T.muted }} data-testid={`italy-forecast-none-${item.indicator}`}>{t("fc_none")}</p>}
      <SrTable caption={title} columns={[t("rank_col_year"), t("fc_observed"), t("fc_median"), t("fc_band")]}
        rows={rows.map((r) => [r.period, fmtCompact(r.value, lang), fmtCompact(r.fq50, lang),
          r.band ? `${fmtCompact(r.band[0], lang)} – ${fmtCompact(r.band[1], lang)}` : "—"])} />
    </div>
  );
};

/** M4 — Banca d'Italia series continued by the forecast in force, three views:
 *  regions (key = ITTER107 code, 4 quarters), origins (ISO3, 4 quarters), national (12 months). */
export default function ItalyForecastPanel({ regions, dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [view, setView] = useState("regions");
  const [region, setRegion] = useState("ITC4");
  const [origin, setOrigin] = useState("DEU");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const path = view === "regions" ? `italy/forecast/regions/${region}`
    : view === "origins" ? `italy/forecast/origins/${origin}` : "italy/forecast/national";

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet(path, { lang })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [path, lang]);

  const selector = view === "regions" ? (
    <select value={region} onChange={(e) => setRegion(e.target.value)} aria-label={t("fc_region_select")}
      data-testid="italy-forecast-region-select" className="text-xs px-2 py-1.5 outline-none"
      style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }}>
      {(regions || []).map((r) => <option key={r.code} value={r.code}>{regionName(r.code, lang, r.label)}</option>)}
    </select>
  ) : view === "origins" ? (
    <select value={origin} onChange={(e) => setOrigin(e.target.value)} aria-label={t("fc_origin_select")}
      data-testid="italy-forecast-origin-select" className="text-xs px-2 py-1.5 outline-none"
      style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }}>
      {ORIGINS.map(([code, en]) => <option key={code} value={code}>{lang === "it" ? (ORIGIN_NAMES_IT[code] || en) : en}</option>)}
    </select>
  ) : null;

  const cols = (data?.items || []).length >= 4 ? "lg:grid-cols-2 xl:grid-cols-4" : "lg:grid-cols-3";
  return (
    <section className="mb-10" data-testid="italy-forecast-panel" aria-labelledby="italy-fc-title">
      <SectionTitle dm={dm} right={selector}>
        <span id="italy-fc-title">{t("fc_italy_title")}</span>
      </SectionTitle>
      <p className="text-xs mb-3" style={{ color: T.muted }}>{t("fc_italy_sub")}</p>
      <div className="flex items-center gap-1 mb-4 flex-wrap" role="tablist" aria-label={t("fc_italy_title")}>
        {VIEWS.map((v) => (
          <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)}
            data-testid={`italy-forecast-view-${v}`}
            className="text-xs font-bold uppercase tracking-wider px-3 py-1.5"
            style={{ background: view === v ? T.brand : T.surface, color: view === v ? "#fff" : T.muted,
              border: `1px solid ${view === v ? T.brand : T.border}`, borderRadius: 2 }}>
            {t(`fc_view_${v}`)}
          </button>
        ))}
        <Link to={`/pulse/forecast-accuracy${lang === "it" ? "?lang=it" : ""}`} className="ml-auto text-xs font-semibold underline"
          style={{ color: T.accent }} data-testid="italy-forecast-accuracy-link">{t("fc_accuracy_link")} →</Link>
      </div>
      {loading && <SkeletonCard h={260} dm={dm} />}
      {!loading && !data && <NoData dm={dm} h={160} testid="italy-forecast-no-data" />}
      {!loading && data && (
        <>
          <div className={`grid grid-cols-1 ${cols} gap-5`}>
            {data.items.map((it) => <MiniForecastChart key={it.indicator} item={it} dm={dm} lang={lang} t={t} />)}
          </div>
          {!data.run && <p className="text-[11px] mt-2" style={{ color: T.muted }} data-testid="italy-forecast-no-run">{t("fc_no_run")}</p>}
        </>
      )}
    </section>
  );
}
